/**
 * SDMS "…-Oriented Summary" sheets — the post-merge fill OG applies after GenerateMailMerge
 * (SpreadsheetEntryController :4636-4665 Model 0, :3831-3836 Model 1) and G6 never ported, so those
 * sheets came back BLANK on every open (parity harness class 1, 2026-09-23).
 *
 * OG gates each sheet on the template's Traceability naming it AND the sheet existing, then fills it
 * with a small pivot of the batch: one line per sample (Sample-Oriented) or one line per parameter
 * (Parameter-Oriented) whose cells are FORMULAS into the merged result sheet(s), so the summary follows
 * the analyst's edits live. Five OG routines, ported one-to-one (formula text emitted in the shape the
 * OG blobs carry — DevExpress-normalised: upper-case function names, sheet names quoted only when
 * needed):
 *
 *   Model 0 (one result sheet, blocks of rows per sample in column A):
 *     TransformDataTableParameterSheet   (:29837)  Parameter-Oriented
 *     TransformDataTableSampleSheet      (:30361)  Sample-Oriented
 *     TransformDataTableSampleSheet_PFAS_533 (:30874)  Sample-Oriented when the TEST is "PFAS_533"
 *   Model 1 (one sheet per run, named by the run's code):
 *     TransformDataTable                 (:29054)  Parameter-Oriented
 *     TransformDataTablebySample         (:29426)  Sample-Oriented
 *
 * Quirks reproduced on purpose (they are what the saved OG blobs hold):
 *   • Model-0 Parameter-Oriented looks the File-Name field up with `.ToUpper() == "UserDefined2"`, which
 *     never matches → that row stays the STATIC UserDefined2 values; the Sample-Oriented variant matches
 *     and writes the IFERROR(INDEX(..MATCH..)) formula.
 *   • Model-0 ranges end at the used range's 0-based BottomRowIndex used as a 1-based row (one short);
 *     Model-1 ranges end at BottomRowIndex + 1 (the true last row).
 *   • The summary sheets are moved to the END of the workbook (Parameter-Oriented first, so
 *     Sample-Oriented ends up last).
 * Deviation (logged, not silent): a sample whose code never appears in column A of the result sheet
 * (OG would emit `K0:K0`, an invalid reference) gets no formulas for that row.
 */
import { loadSdmsWorkbook, writeSdmsWorkbook } from './sdmsWorkbookIo';
import { applyOgSheetOrder } from './sdmsSheetOrder';

type Row = Record<string, unknown>;
type Ws = import('exceljs').Worksheet;

export const PARAMETER_ORIENTED = 'Parameter-Oriented Summary';
export const SAMPLE_ORIENTED = 'Sample-Oriented Summary';

export interface SummaryOrientationInput {
  /** The merge records (one per run × parameter) — OG's dtsample. */
  records: Row[];
  /** SpreadSheetBuilder_TemplateInfo.Traceability — names the sheets to fill. */
  traceability: string | null | undefined;
  /** TemplateInfo.Model: 0 = one result sheet, 1 = one sheet per run. */
  model: number | null | undefined;
  /** RawDataTable DataParsing positions, FIELD (upper-case) → A1 — OG's drrSelect. */
  fieldCells: Record<string, string> | null | undefined;
  /** The batch's TEST name — "PFAS_533" selects OG's PFAS variant of the Sample-Oriented fill. */
  testName?: string | null;
  /** Optional sink for the logged deviations. */
  notes?: string[];
}

// ── small helpers ─────────────────────────────────────────────────────────────

const normKey = (k: string) => k.toUpperCase().replace(/^\[(.+)\]$/, '$1');
/** Each record's FIRST key per normalised name, while applySummaryOrientationToWorkbook runs (null otherwise): a
 *  reopen record carries ~440 keys and every read regex-normalised all of them (speed, 2026-09-30). */
let pickIndex: WeakMap<object, Map<string, string>> | null = null;
const pick = (rec: Row, key: string): unknown => {
  if (!pickIndex) { for (const k of Object.keys(rec)) if (normKey(k) === key) return rec[k]; return undefined; }
  let first = pickIndex.get(rec);
  if (!first) {
    first = new Map();
    for (const k of Object.keys(rec)) { const n = normKey(k); if (!first.has(n)) first.set(n, k); }
    pickIndex.set(rec, first);
  }
  const k = first.get(key);
  return k === undefined ? undefined : rec[k];
};
const str = (v: unknown): string => (v == null ? '' : v instanceof Date ? fmtDate(v) : String(v)).trim();
const fmtDate = (d: Date) => {
  const p2 = (n: number) => String(n).padStart(2, '0');
  return `${p2(d.getMonth() + 1)}/${p2(d.getDate())}/${d.getFullYear()} ${p2(d.getHours())}:${p2(d.getMinutes())}`;   // OG "MM/dd/yyyy HH:mm"
};

export function colLetter(col1: number): string {
  let s = ''; let n = col1;
  while (n > 0) { const m = (n - 1) % 26; s = String.fromCharCode(65 + m) + s; n = Math.floor((n - 1) / 26); }
  return s;
}
const colOf = (a1: string) => { const m = /^([A-Z]+)/i.exec(a1.trim()); return m ? m[1]!.toUpperCase() : ''; };
const rowOf = (a1: string) => { const m = /(\d+)/.exec(a1.trim()); return m ? Number(m[1]) : 0; };

/** A1-style sheet reference — quoted only when the name needs it (DevExpress writes "Sheet1!A1" but
 *  "'CCV1'!A1": a name that parses as a cell reference, or holds a space/dash, must be quoted). */
export function sheetRef(name: string): string {
  const plain = /^[A-Za-z_][A-Za-z0-9_]*$/.test(name) && !/^[A-Za-z]{1,3}\d+$/.test(name);
  return plain ? name : `'${name.replace(/'/g, "''")}'`;
}

/** Distinct values in first-seen order. */
const distinct = (xs: string[]) => { const seen = new Set<string>(); const out: string[] = []; for (const x of xs) if (x && !seen.has(x)) { seen.add(x); out.push(x); } return out; };

/** 1-based first/last row of a sample's block in column A of the result sheet (OG dtSheet1 "Column1"
 *  scan, case-insensitive), or null when the code never appears. */
function blockRows(ws: Ws, code: string): { first: number; last: number } | null {
  const want = code.toUpperCase();
  let first = 0, last = 0;
  const lastRow = lastUsedRow(ws);
  for (let r = 1; r <= lastRow; r++) {
    const v = ws.getRow(r).getCell(1).value;
    const t = str(v && typeof v === 'object' && 'result' in (v as object) ? (v as { result: unknown }).result : v).toUpperCase();
    if (t === want) { if (!first) first = r; last = r; }
  }
  return first ? { first, last } : null;
}

/** 1-based number of the last row holding any cell — OG's GetUsedRange().BottomRowIndex + 1.
 *  (ExcelJS `actualRowCount` is a COUNT of populated rows, not the last row number.) */
const lastUsedRow = (ws: Ws): number => { let last = 0; ws.eachRow({ includeEmpty: false }, (_r, n) => { if (n > last) last = n; }); return last; };

const setF = (ws: Ws, a1: string, formula: string, numFmt?: string) => {
  const c = ws.getCell(a1);
  c.value = { formula } as never;
  if (numFmt) c.style = { ...c.style, numFmt };   // clone: ExcelJS shares style objects between cells (see sdmsMailMerge setNumFmt)
};
const setV = (ws: Ws, a1: string, v: unknown) => { ws.getCell(a1).value = (v == null || v === '' ? null : v) as never; };
/** Centre ONE cell, as OG's `Cells[..].Alignment.Horizontal = Center` does. The style is CLONED: ExcelJS shares one style
 *  object between every cell of the same template style, so assigning `.alignment` in place centred the whole sheet —
 *  OG's summaries centre only the units row / column and leave the result cells left-aligned (THM 8112, VOC 8086,
 *  PFAS 8114 saved sheets, 2026-09-30). */
const centre = (ws: Ws, a1: string) => { const c = ws.getCell(a1); c.style = { ...c.style, alignment: { ...c.style.alignment, horizontal: 'center' } }; };

const VALID_FORMULA = (ref: string, col: string, first: number, last: number) =>
  `IF(SUMPRODUCT(--(${ref}!${col}${first}:${col}${last} <>"" ))=0,"", IF(COUNTIF(${ref}!${col}${first}:${col}${last},"Fail") > 0,"Fail","Pass"))`;

// ── the transforms ────────────────────────────────────────────────────────────

interface Ctx {
  wb: import('exceljs').Workbook;
  records: Row[];
  samples: string[];
  params: string[];
  firstRec: (code: string) => Row | undefined;
  unitsOf: (param: string) => string;
  pos: (field: string) => string;   // A1 position of a RawDataTable field, '' when unmapped
  notes: string[];
}

/** Model 0 · Parameter-Oriented (OG TransformDataTableParameterSheet). Rows: 2 File Name (static) ·
 *  3 SiteName · 4 Analyzed Date · 5 SysSampleCode · 6 Valid? · 7+ one per parameter; A = label, B = units,
 *  C.. = one column per sample. */
function parameterSheetModel0(ws: Ws, main: Ws, ctx: Ctx): void {
  const S = sheetRef(main.name);
  const bottom0 = lastUsedRow(main) - 1;                      // OG BottomRowIndex (0-based) used as a row
  const unitsPos = ctx.pos('UNITS'), parPos = ctx.pos('PARAMETER'), resPos = ctx.pos('RESULT');
  const adPos = ctx.pos('ANALYZEDDATE'), qcPos = ctx.pos('QCSTATUS'), sitePos = ctx.pos('SITENAME');
  const labels = ['File Name', 'SiteName', 'Analyzed Date', 'SysSampleCode', 'Valid?'];
  labels.forEach((l, i) => setV(ws, `A${2 + i}`, l));
  ctx.samples.forEach((code, i) => {
    const col = colLetter(3 + i);
    const rec = ctx.firstRec(code);
    // static import values (File Name stays static — see the class remark; the others get formulas below)
    setV(ws, `${col}2`, rec ? str(pick(rec, 'USERDEFINED2')) : '');
    setV(ws, `${col}3`, rec ? str(pick(rec, 'SITENAME')) : '');
    setV(ws, `${col}4`, rec ? str(pick(rec, 'ANALYZEDDATE')) : '');
    setV(ws, `${col}5`, code);
    setV(ws, `${col}6`, rec ? str(pick(rec, 'QCSTATUS')) : '');
    const blk = blockRows(main, code);
    if (!blk) { ctx.notes.push(`${PARAMETER_ORIENTED}: sample ${code} not found in column A of ${main.name} — row left static`); return; }
    if (adPos) setF(ws, `${col}4`, `IFERROR(INDEX(${S}!${colOf(adPos)}:${colOf(adPos)}, MATCH("${code}", ${S}!A:A, 0)), "")`);
    if (qcPos) setF(ws, `${col}6`, VALID_FORMULA(S, colOf(qcPos), blk.first, blk.last));
    if (sitePos) setF(ws, `${col}3`, `INDEX(${S}!${colOf(sitePos)}${blk.first}:${colOf(sitePos)}${blk.last}, 1)`);
  });
  ctx.params.forEach((param, pi) => {
    const row = 7 + pi;
    setV(ws, `A${row}`, param);
    if (unitsPos && parPos) {
      setF(ws, `B${row}`, `INDEX(${S}!${colOf(unitsPos)}${rowOf(unitsPos)}:${colOf(unitsPos)}${bottom0}, MATCH(A${row},${S}!${colOf(parPos)}${rowOf(unitsPos)}:${colOf(parPos)}${bottom0},0))`);
      centre(ws, `B${row}`);
    }
    ctx.samples.forEach((code, i) => {
      const blk = blockRows(main, code);
      if (!blk || !resPos || !parPos) return;
      setF(ws, `${colLetter(3 + i)}${row}`, `INDEX(${S}!${colOf(resPos)}${blk.first}:${colOf(resPos)}${blk.last}, MATCH(A${row},${S}!${colOf(parPos)}${blk.first}:${colOf(parPos)}${blk.last},0))`);
    });
  });
}

/** Model 0 · Sample-Oriented (OG TransformDataTableSampleSheet; PFAS_533 variant when `pfas`). Row 2 =
 *  headers, row 3 = units, rows 4+ = one per sample: B File Name · C Site Name · D Analyzed Date ·
 *  E Valid? · F.. one column per parameter. */
function sampleSheetModel0(ws: Ws, main: Ws, ctx: Ctx, pfas: boolean): void {
  const S = sheetRef(main.name);
  const bottom0 = lastUsedRow(main) - 1;
  const unitsPos = ctx.pos('UNITS'), parPos = ctx.pos('PARAMETER'), resPos = ctx.pos('RESULT');
  const adPos = ctx.pos('ANALYZEDDATE'), qcPos = ctx.pos('QCSTATUS');
  const filePos = pfas ? '' : ctx.pos('USERDEFINED2');
  const sitePos = pfas ? ctx.pos('USERDEFINED8') : ctx.pos('SITENAME');
  const typePos = pfas ? ctx.pos('USERDEFINED7') : '';
  const heads = pfas ? ['SysSampleCode', 'Sample Type', 'SiteName', 'Acquisition Date/Time', 'Valid?']
                     : ['SysSampleCode', 'File Name', 'Site Name', 'Analyzed Date', 'Valid?'];
  heads.forEach((h, i) => setV(ws, `${colLetter(1 + i)}2`, h));
  ctx.params.forEach((param, pi) => {
    const col = colLetter(6 + pi);
    setV(ws, `${col}2`, param);
    if (unitsPos && parPos) {
      setF(ws, `${col}3`, `INDEX(${S}!${colOf(unitsPos)}${rowOf(unitsPos)}:${colOf(unitsPos)}${bottom0}, MATCH("${param}",${S}!${colOf(parPos)}${rowOf(unitsPos)}:${colOf(parPos)}${bottom0},0))`);
      centre(ws, `${col}3`);
    }
  });
  ctx.samples.forEach((code, i) => {
    const row = 4 + i;
    setV(ws, `A${row}`, code);
    const blk = blockRows(main, code);
    if (!blk) { ctx.notes.push(`${SAMPLE_ORIENTED}: sample ${code} not found in column A of ${main.name} — row left with its code only`); return; }
    if (filePos) setF(ws, `B${row}`, `IFERROR(INDEX(${S}!${colOf(filePos)}:${colOf(filePos)}, MATCH("${code}", ${S}!A:A, 0)), "")`);
    if (typePos) setF(ws, `B${row}`, `INDEX(${S}!${colOf(typePos)}${blk.first}:${colOf(typePos)}${blk.last},1)`);
    if (sitePos) setF(ws, `C${row}`, `INDEX(${S}!${colOf(sitePos)}${blk.first}:${colOf(sitePos)}${blk.last},1)`);
    if (adPos) setF(ws, `D${row}`, `IFERROR(INDEX(${S}!${colOf(adPos)}:${colOf(adPos)}, MATCH("${code}", ${S}!A:A, 0)), "")`);
    if (qcPos) setF(ws, `E${row}`, VALID_FORMULA(S, colOf(qcPos), blk.first, blk.last));
    if (resPos && parPos) ctx.params.forEach((param, pi) =>
      setF(ws, `${colLetter(6 + pi)}${row}`, `INDEX(${S}!${colOf(resPos)}${blk.first}:${colOf(resPos)}${blk.last}, MATCH("${param}",${S}!${colOf(parPos)}${blk.first}:${colOf(parPos)}${blk.last},0))`, pfas ? '0.00' : undefined));
  });
}

/** The per-run sheet for a code on a Model-1 workbook (the merge names it by the code, sanitised). */
function runSheet(wb: import('exceljs').Workbook, code: string): Ws | undefined {
  const base = code.replace(/[[\]:*?/\\]/g, '').trim().slice(0, 31);
  return wb.getWorksheet(code) ?? wb.getWorksheet(base) ?? wb.worksheets.find(w => w.name.toUpperCase() === base.toUpperCase());
}

/** Model 1 · Parameter-Oriented (OG TransformDataTable). Row 2 = "SysSampleCode" + codes from C; rows 3-6
 *  File Name · SiteName · Analyzed Date · Valid? (direct references into each run's sheet); 7+ params with
 *  static units in B and INDEX/MATCH results per run. */
function parameterSheetModel1(ws: Ws, ctx: Ctx): void {
  const filePos = ctx.pos('USERDEFINED2'), sitePos = ctx.pos('SITENAME'), adPos = ctx.pos('ANALYZEDDATE'), qcPos = ctx.pos('QCSTATUS');
  const resPos = ctx.pos('RESULT'), parPos = ctx.pos('PARAMETER');
  setV(ws, 'A2', 'SysSampleCode');
  ['File Name', 'SiteName', 'Analyzed Date', 'Valid?'].forEach((l, i) => setV(ws, `A${3 + i}`, l));
  ctx.samples.forEach((code, i) => {
    const col = colLetter(3 + i);
    setV(ws, `${col}2`, code);
    const rs = runSheet(ctx.wb, code);
    if (!rs) { ctx.notes.push(`${PARAMETER_ORIENTED}: no run sheet for ${code}`); return; }
    const Q = sheetRef(rs.name), last = lastUsedRow(rs);
    if (filePos) setF(ws, `${col}3`, `${Q}!${filePos}`);
    if (sitePos) setF(ws, `${col}4`, `${Q}!${sitePos}`);
    if (adPos) setF(ws, `${col}5`, `${Q}!${adPos}`);
    if (qcPos) setF(ws, `${col}6`, `IF(SUMPRODUCT(--(${Q}!${qcPos}:${colOf(qcPos)}${last} <>"" ))=0,"", IF(COUNTIF(${Q}!${qcPos}:${colOf(qcPos)}${last},"Fail") > 0,"Fail","Pass"))`);
  });
  ctx.params.forEach((param, pi) => {
    const row = 7 + pi;
    setV(ws, `A${row}`, param);
    setV(ws, `B${row}`, ctx.unitsOf(param));
    centre(ws, `B${row}`);
    ctx.samples.forEach((code, i) => {
      const rs = runSheet(ctx.wb, code);
      if (!rs || !resPos || !parPos) return;
      const Q = sheetRef(rs.name), last = lastUsedRow(rs);
      setF(ws, `${colLetter(3 + i)}${row}`, `INDEX(${Q}!${resPos}:${colOf(resPos)}${last}, MATCH(A${row}, ${Q}!${parPos}:${colOf(parPos)}${last}, 0))`);
    });
  });
}

/** Model 1 · Sample-Oriented (OG TransformDataTablebySample). Row 2 headers, row 3 static units, rows 4+
 *  one per run: B File Name · C Site Name · D Analyzed Date (direct refs) · E Valid? · F.. INDEX/MATCH. */
function sampleSheetModel1(ws: Ws, ctx: Ctx): void {
  const filePos = ctx.pos('USERDEFINED2'), sitePos = ctx.pos('SITENAME'), adPos = ctx.pos('ANALYZEDDATE'), qcPos = ctx.pos('QCSTATUS');
  const resPos = ctx.pos('RESULT'), parPos = ctx.pos('PARAMETER');
  ['SysSampleCode', 'File Name', 'Site Name', 'Analyzed Date', 'Valid?'].forEach((h, i) => setV(ws, `${colLetter(1 + i)}2`, h));
  ctx.params.forEach((param, pi) => {
    const col = colLetter(6 + pi);
    setV(ws, `${col}2`, param);
    setV(ws, `${col}3`, ctx.unitsOf(param));
    centre(ws, `${col}3`);
  });
  ctx.samples.forEach((code, i) => {
    const row = 4 + i;
    setV(ws, `A${row}`, code);
    const rs = runSheet(ctx.wb, code);
    if (!rs) { ctx.notes.push(`${SAMPLE_ORIENTED}: no run sheet for ${code}`); return; }
    const Q = sheetRef(rs.name), last = lastUsedRow(rs);
    if (filePos) setF(ws, `B${row}`, `${Q}!${filePos}`);
    if (sitePos) setF(ws, `C${row}`, `${Q}!${sitePos}`);
    if (adPos) setF(ws, `D${row}`, `${Q}!${adPos}`);
    if (qcPos) setF(ws, `E${row}`, `IF(SUMPRODUCT(--(${Q}!${qcPos}:${colOf(qcPos)}${last} <>"" ))=0,"", IF(COUNTIF(${Q}!${qcPos}:${colOf(qcPos)}${last},"Fail") > 0,"Fail","Pass"))`);
    if (resPos && parPos) ctx.params.forEach((param, pi) =>
      setF(ws, `${colLetter(6 + pi)}${row}`, `INDEX(${Q}!${resPos}:${colOf(resPos)}${last}, MATCH("${param.replace(/"/g, '""')}", ${Q}!${parPos}:${colOf(parPos)}${last}, 0))`));
  });
}

// ── entry point ───────────────────────────────────────────────────────────────

/** Fill the "…-Oriented Summary" sheets of a MERGED workbook the way OG does after its mail merge.
 *  Returns the same buffer when the template's Traceability names neither sheet or neither exists. */
export async function applySummaryOrientation(buf: ArrayBuffer, inp: SummaryOrientationInput): Promise<ArrayBuffer> {
  if (!wantsSummaryOrientation(inp)) return buf;
  const wb = await loadSdmsWorkbook(buf);
  return applySummaryOrientationToWorkbook(wb, inp) ? writeSdmsWorkbook(wb) : buf;
}

function wantsSummaryOrientation(inp: SummaryOrientationInput): boolean {
  const trace = inp.traceability ?? '';
  return trace.includes(PARAMETER_ORIENTED) || trace.includes(SAMPLE_ORIENTED);
}

/** The same fill on a workbook already in memory (the open chain — sdmsWorkbookIo.ts). Returns false,
 *  leaving `wb` untouched, when the Traceability names neither sheet or neither exists. */
export function applySummaryOrientationToWorkbook(wb: import('exceljs').Workbook, inp: SummaryOrientationInput): boolean {
  // The key index lives only while this (synchronous) fill runs: the records are not changed during it, and a
  // caller may add keys to them between two fills.
  const prev = pickIndex;
  pickIndex = new WeakMap();
  try { return fillSummaryOrientation(wb, inp); } finally { pickIndex = prev; }
}

function fillSummaryOrientation(wb: import('exceljs').Workbook, inp: SummaryOrientationInput): boolean {
  if (!wantsSummaryOrientation(inp)) return false;
  const trace = inp.traceability ?? '';
  const wantP = trace.includes(PARAMETER_ORIENTED), wantS = trace.includes(SAMPLE_ORIENTED);
  const wsP = wantP ? wb.getWorksheet(PARAMETER_ORIENTED) : undefined;
  const wsS = wantS ? wb.getWorksheet(SAMPLE_ORIENTED) : undefined;
  if (!wsP && !wsS) return false;

  const fc = inp.fieldCells ?? {};
  const pos = (field: string) => { for (const k of Object.keys(fc)) if (normKey(k) === field) return (fc[k] ?? '').trim().toUpperCase(); return ''; };
  const records = inp.records ?? [];
  const codeOf = records.map(r => str(pick(r, 'SYSSAMPLECODE')));
  const paramOf = records.map(r => str(pick(r, 'PARAMETER')));
  const samples = distinct(codeOf);
  const params = distinct(paramOf);
  // The FIRST record per sample code / parameter — what records.find returned, without a scan per lookup.
  const firstByCode = new Map<string, Row>(), firstByParam = new Map<string, Row>();
  records.forEach((r, i) => {
    if (!firstByCode.has(codeOf[i]!)) firstByCode.set(codeOf[i]!, r);
    if (!firstByParam.has(paramOf[i]!)) firstByParam.set(paramOf[i]!, r);
  });
  const ctx: Ctx = {
    wb, records, samples, params,
    firstRec: code => firstByCode.get(code),
    unitsOf: param => { const r = firstByParam.get(param); return r ? str(pick(r, 'UNITS')) : ''; },
    pos, notes: inp.notes ?? [],
  };
  const model = inp.model ?? 0;
  const main = wb.worksheets[0]!;
  const pfas = (inp.testName ?? '').trim() === 'PFAS_533';

  if (wsP) { if (model === 1) parameterSheetModel1(wsP, ctx); else parameterSheetModel0(wsP, main, ctx); }
  if (wsS) { if (model === 1) sampleSheetModel1(wsS, ctx); else sampleSheetModel0(wsS, main, ctx, pfas); }

  // OG's tab order (its MoveToEnd sequence per model — sdmsSheetOrder.ts): on Model 0 the two summaries go last,
  // on Model 1 they sit BEFORE Reagents / Instruments / Raw Data (OG vs AI VOC comparison 2026-09-28).
  applyOgSheetOrder(wb, model);
  return true;
}
